import { create } from "zustand"
import { productsService, type FetchPlansOptions } from "../api"
import type { ProductPlan } from "../types"

export type PlansStatus = "idle" | "loading" | "ready" | "error"

interface PlansState {
  plansById: Record<string, ProductPlan>
  status: PlansStatus
  error: string | null
  loadPlans: (ids: string[], options?: FetchPlansOptions) => Promise<ProductPlan[]>
  getPlan: (id: string) => ProductPlan | undefined
}

/**
 * In-memory cache of fetched plans. Acts as a single source of truth so that
 * checkout, upsells and the webapp can read the same plan objects without refetching.
 */
export const usePlansStore = create<PlansState>((set, get) => ({
  plansById: {},
  status: "idle",
  error: null,

  loadPlans: async (ids, options) => {
    const requested = Array.from(new Set(ids.filter(Boolean)))
    if (requested.length === 0) return []

    set({ status: "loading", error: null })

    try {
      const plans = await productsService.fetchPlans(requested, options)

      set((state) => ({
        status: "ready",
        plansById: {
          ...state.plansById,
          ...Object.fromEntries(plans.map((plan) => [plan.id, plan])),
        },
      }))

      return plans
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load plans"
      set({ status: "error", error: message })
      return []
    }
  },

  getPlan: (id) => get().plansById[id],
}))
